import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAgpQuery } from "../hooks/useAgpApi";
import { AttainmentBar, PaceChip, agpWindowLabel, formatTarget } from "./AgpUi";
import { TeamDot } from "../../components/Ui";
import SourceTip from "../../components/SourceTip";
import { useSources } from "../../hooks/useBdApi";
import { currentEtYear } from "../../hooks/usePeriod";

const YEAR = currentEtYear();

// AGP progress is annual: always Jan 1 -> today, independent of the period
// selected in the performance pages.
function AgpWindowNote({ window }) {
  return (
    <span className="bd-agp-strip__window bd-muted" title="Annual goals are measured from Jan 1 of the AGP year to today, regardless of the period selected above">
      {window ? agpWindowLabel(window) : `Jan 1 – today`} · not affected by the period filter
    </span>
  );
}

function DriverTip({ driverKey, label }) {
  const { sources } = useSources();
  const metric = sources && sources.agpDrivers ? sources.agpDrivers[driverKey] : null;
  if (!metric) return null;
  return <SourceTip metric={metric} title={`AGP - ${label}`} />;
}

/**
 * Compact "AGP pace" strip for the performance pages. Pass either `teamKey`
 * (team scope) or `sfUserId` (member scope). Renders nothing when the scope
 * has no goals for the current year.
 */
export function AgpPaceStrip({ teamKey, sfUserId }) {
  const params = useMemo(() => ({ year: YEAR, ...(sfUserId ? { sfUserId } : { team: teamKey }) }), [teamKey, sfUserId]);
  const { data } = useAgpQuery("summary", params);
  if (!data || !data.hasGoals || !data.drivers.length) return null;
  const to = sfUserId ? `/landmark/bd/agp/member/${sfUserId}` : `/landmark/bd/agp/team/${data.teamKey}`;
  return (
    <section className="bd-agp-strip">
      <div className="bd-agp-strip__head">
        <span className="bd-agp-strip__title">
          AGP {YEAR} progress <AgpWindowNote window={data.window} />
        </span>
        <Link to={to} className="bd-link">
          View goals →
        </Link>
      </div>
      <div className="bd-agp-strip__items">
        {data.drivers.map((d) => (
          <Link key={d.key} to={to} className="bd-agp-strip__item">
            <div className="bd-agp-strip__label">
              <span>
                {d.label} <DriverTip driverKey={d.key} label={d.label} />
              </span>
              <PaceChip row={d} compact />
            </div>
            <div className="bd-agp-strip__value">
              <strong>{formatTarget(d.actual, d.format)}</strong>
              <span className="bd-muted"> / {formatTarget(d.target, d.format)}</span>
            </div>
            <AttainmentBar row={d} />
          </Link>
        ))}
      </div>
    </section>
  );
}

/**
 * Org-level strip for the performance overview: one row per practice with the
 * headline drivers.
 */
export function AgpOrgStrip() {
  const params = useMemo(() => ({ year: YEAR }), []);
  const { data } = useAgpQuery("overview", params);
  if (!data) return null;
  // Headline drivers per practice. grossRevenue is the Financial goal's
  // revenue driver (target from the workbook, actual from manual entries).
  const keys = ["clientMeetings", "taps", "dealsClosed", "curateAttendees", "grossRevenue"];
  // Practices with goals for at least one headline driver (Client Acquisition
  // has its own driver set and its own section on the overview).
  const teams = data.teams.filter((t) => t.hasGoals && t.drivers.some((d) => keys.includes(d.key) && d.target !== null && d.target !== undefined));
  if (!teams.length) return null;
  return (
    <section className="bd-agp-strip bd-agp-strip--org">
      <div className="bd-agp-strip__head">
        <span className="bd-agp-strip__title">
          AGP {YEAR} progress by practice <AgpWindowNote window={data.window} />
        </span>
        <Link to="/landmark/bd/agp" className="bd-link">
          Open AGP goals →
        </Link>
      </div>
      <div className="bd-table-wrap bd-table-wrap--dense">
        <table className="bd-table bd-agp-table">
          <thead>
            <tr>
              <th>Practice</th>
              {keys.map((k) => {
                const def = data.catalog.find((c) => c.key === k) || {};
                return (
                  <th key={k} style={{ textAlign: "right" }}>
                    <span>
                      {def.label} <DriverTip driverKey={k} label={def.label} />
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {teams.map((t) => (
              <tr key={t.key}>
                <td>
                  <Link to={`/landmark/bd/agp/team/${t.key}`} className="bd-link">
                    <TeamDot team={t.name} /> {t.practice}
                  </Link>
                </td>
                {keys.map((k) => {
                  const d = t.drivers.find((x) => x.key === k);
                  if (!d || d.target === null) {
                    return (
                      <td key={k} style={{ textAlign: "right" }} className="bd-muted">
                        —
                      </td>
                    );
                  }
                  const noActual = d.actual === null || d.actual === undefined;
                  const showClosedWon = k === "grossRevenue" && t.closedWon && t.closedWon.amount > 0;
                  return (
                    <td key={k} style={{ textAlign: "right" }}>
                      <span className="bd-agp-strip__inline">
                        {noActual ? <span className="bd-muted">—</span> : formatTarget(d.actual, d.format)}
                        <span className="bd-muted"> / {formatTarget(d.target, d.format)}</span>
                        <PaceChip row={d} compact />
                      </span>
                      {showClosedWon && (
                        <div>
                          <small className="bd-muted" title="Salesforce closed-won revenue Jan 1 - today (Closed Won Oppor report), for reference">
                            SF closed-won {formatTarget(t.closedWon.amount, "currency")}
                          </small>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
